// Pure rules behind the mandates editor (kanban 838512f8): the draft an admin
// edits and the request it becomes.

import type { MandateAreaInput, TeamMandate, TeamMandateRequest } from '../../services/roadmapReports';

export interface AreaDraft {
  key: string | null;
  title: string;
  description: string;
}

export interface MandateDraft {
  areas: AreaDraft[];
  basis: string;
  /** The team's high-level definition (kanban 16a22d06), as typed. */
  definition: string;
  /** The team's charter verbatim (kanban 87ab737b), as typed. */
  charter: string;
}

export const MAX_AREAS = 12;
export const MAX_DEFINITION = 2000;
export const MAX_CHARTER = 20000;

export function draftFrom(m: TeamMandate | null): MandateDraft {
  return {
    areas: (m?.areas ?? []).map((a) => ({ key: a.key, title: a.title, description: a.description })),
    basis: m?.basis ?? '',
    definition: m?.definition ?? '',
    charter: m?.charter ?? '',
  };
}

export function addArea(d: MandateDraft): MandateDraft {
  if (d.areas.length >= MAX_AREAS) return d;
  return { ...d, areas: [...d.areas, { key: null, title: '', description: '' }] };
}

export function removeArea(d: MandateDraft, n: number): MandateDraft {
  return { ...d, areas: d.areas.filter((_, i) => i !== n) };
}

export function moveArea(d: MandateDraft, n: number, by: -1 | 1): MandateDraft {
  const to = n + by;
  if (to < 0 || to >= d.areas.length) return d;
  const areas = [...d.areas];
  [areas[n], areas[to]] = [areas[to], areas[n]];
  return { ...d, areas };
}

export function updateArea(d: MandateDraft, n: number, patch: Partial<AreaDraft>): MandateDraft {
  return { ...d, areas: d.areas.map((a, i) => (i === n ? { ...a, ...patch } : a)) };
}

/** Blank rows are dropped; a row that was renamed keeps its key so filed
 * initiatives stay filed. The definition and the charter always travel
 * (null when blank), so clearing either in the editor clears it on the
 * record. */
export function toRequest(d: MandateDraft): TeamMandateRequest {
  const areas: MandateAreaInput[] = d.areas
    .filter((a) => a.title.trim() !== '')
    .map((a) => ({ key: a.key, title: a.title.trim(), description: a.description.trim() }));
  return { areas, basis: d.basis.trim() || null, definition: d.definition.trim() || null, charter: d.charter.trim() || null };
}

/** Does the draft say anything the stored mandate does not? */
export function dirty(d: MandateDraft, m: TeamMandate | null): boolean {
  const a = toRequest(d);
  const b = toRequest(draftFrom(m));
  return JSON.stringify(a) !== JSON.stringify(b);
}

/** The team name typed for a new task force, folded like the backend does. */
export function cleanTeamName(raw: string): string | null {
  const name = raw.split(/\s+/).filter(Boolean).join(' ');
  if (!name || name.length > 40) return null;
  return name;
}
